import { useEffect } from "react";

/*
 * Lightweight per-route SEO manager (no external dep). Sets:
 *   - document.title
 *   - meta description
 *   - canonical link
 *   - Open Graph (title, description, url, image, type)
 *   - Twitter Card (title, description, image)
 *   - Optional JSON-LD structured-data block (Article, BreadcrumbList, etc.)
 *
 * Every mutation is scoped by a `data-managed-by="seo-hook"` attribute so we can safely reset
 * on unmount — the base <head> tags in public/index.html (defaults for the Organization schema,
 * generic OG image, etc.) are never touched.
 *
 * Usage:
 *   <Seo
 *     title="Management Consulting for Small Business | Upland Group"
 *     description="…"
 *     canonical="https://uplandgroupllc.com/"
 *     ogImage="https://uplandgroupllc.com/upland-logo-full.png"
 *     jsonLd={[{ "@type": "Article", … }]}
 *   />
 */

const MANAGED = "seo-hook";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(`meta[${selector}][data-managed-by="${MANAGED}"]`);
  if (!el) {
    // Prefer replacing an existing tag from index.html (so we don't have both defaults + overrides).
    el = document.head.querySelector(`meta[${selector}]`);
    if (!el) {
      el = document.createElement("meta");
      document.head.appendChild(el);
    }
    el.setAttribute("data-managed-by", MANAGED);
  }
  Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"][data-managed-by="${MANAGED}"]`);
  if (!el) {
    el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      document.head.appendChild(el);
    }
    el.setAttribute("data-managed-by", MANAGED);
  }
  el.setAttribute("href", href);
}

function clearManagedJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][data-managed-by="${MANAGED}"]`)
    .forEach((n) => n.remove());
}

function addJsonLd(blocks) {
  clearManagedJsonLd();
  if (!blocks) return;
  const list = Array.isArray(blocks) ? blocks : [blocks];
  list.forEach((b) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-managed-by", MANAGED);
    s.text = JSON.stringify({ "@context": "https://schema.org", ...b });
    document.head.appendChild(s);
  });
}

// Base values that every route falls back to when a prop is unset. Keeps stale meta from a
// previously-visited route from leaking across navigations (e.g., an Insight detail page
// setting og:image to the article thumbnail, then the next page rendering with that thumbnail).
const DEFAULT_TITLE = "Management Consulting for Small Business | Upland Group";
const DEFAULT_DESC = "Management consulting that pairs high-touch strategy with data-driven, technology-forward execution — built for small and mid-sized businesses ready to scale.";
const DEFAULT_OG_IMAGE = "https://uplandgroupllc.com/upland-logo-full.png";

export default function Seo({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const effectiveTitle = title || DEFAULT_TITLE;
    const effectiveDesc = description || DEFAULT_DESC;
    const effectiveImage = ogImage || DEFAULT_OG_IMAGE;
    document.title = effectiveTitle;
    upsertMeta('name="description"', { name: "description", content: effectiveDesc });
    upsertMeta('name="twitter:description"', { name: "twitter:description", content: effectiveDesc });
    upsertMeta('property="og:description"', { property: "og:description", content: effectiveDesc });
    upsertMeta('property="og:title"', { property: "og:title", content: effectiveTitle });
    upsertMeta('name="twitter:title"', { name: "twitter:title", content: effectiveTitle });
    if (canonical) {
      upsertLink("canonical", canonical);
      upsertMeta('property="og:url"', { property: "og:url", content: canonical });
    }
    upsertMeta('property="og:image"', { property: "og:image", content: effectiveImage });
    upsertMeta('name="twitter:image"', { name: "twitter:image", content: effectiveImage });
    upsertMeta('property="og:type"', { property: "og:type", content: ogType });
    upsertMeta('name="robots"', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });
    addJsonLd(jsonLd);
  }, [title, description, canonical, ogImage, ogType, jsonLd, noindex]);

  return null;
}
